import { z } from "zod";

/**
 * B2B-Lieferungen: Ware an Händler/Zentrallager statt an Endkunden – per Spedition oder Paket, mit Liefertermin,
 * Bestellnummer des Händlers auf dem Lieferschein und Vorgaben des Händlers (MHD, ganze Kartons, Charge).
 * Abnehmer werden als Stammdaten gepflegt; der Auftrag bekommt eine Momentaufnahme (spätere Änderungen am
 * Abnehmer verändern bereits angelegte Lieferungen nicht).
 */

/** GLN (Global Location Number, 13 Stellen) mit Prüfziffer nach GS1 */
export function isValidGln(v: string): boolean {
  if (!/^\d{13}$/.test(v)) return false;
  const digits = v.split("").map(Number);
  const sum = digits.slice(0, 12).reduce((s, d, i) => s + d * (i % 2 === 0 ? 1 : 3), 0);
  return (10 - (sum % 10)) % 10 === digits[12];
}

export const b2bRequirementsSchema = z.object({
  /** Mindest-Restlaufzeit (Tage) bei Ankunft beim Händler – leer = keine Vorgabe */
  minShelfLifeDays: z.number().int().min(1).max(2000).nullable().default(null),
  /** nur ganze Verpackungseinheiten (Karton) */
  fullCartonsOnly: z.boolean().default(false),
  /** je Artikel nur eine Charge */
  singleBatch: z.boolean().default(false),
});
export type B2bRequirements = z.infer<typeof b2bRequirementsSchema>;

export const B2B_SHIPPING = ["freight", "parcel"] as const;
export type B2bShipping = (typeof B2B_SHIPPING)[number];
export const B2B_LOAD_CARRIERS = ["pallet", "carton"] as const;
export type B2bLoadCarrier = (typeof B2B_LOAD_CARRIERS)[number];

const timeRe = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Abnehmer (Händler bzw. dessen Lager/Filiale) */
export const b2bCustomerInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  /** eigene Kundennummer des Abnehmers beim Kunden (erscheint auf dem Lieferschein) */
  customerNumber: z.string().trim().max(40).nullable().default(null),
  gln: z
    .string()
    .trim()
    .nullable()
    .default(null)
    .refine((v) => !v || isValidGln(v), "GLN ungültig (13 Ziffern mit Prüfziffer)"),
  /** Warenannahme, z. B. „Mo–Fr 7–14 Uhr“ */
  receivingHours: z.string().trim().max(200).nullable().default(null),
  /** Anlieferung muss vorab angemeldet werden (Avis/Zeitfenster) */
  avisRequired: z.boolean().default(false),
  /** Ansprechpartner für die Voranmeldung (Name, Telefon, E-Mail) */
  avisContact: z.string().trim().max(300).nullable().default(null),
  defaultShipping: z.enum(B2B_SHIPPING).default("freight"),
  defaultLoadCarrier: z.enum(B2B_LOAD_CARRIERS).default("pallet"),
  requirements: b2bRequirementsSchema.default({ minShelfLifeDays: null, fullCartonsOnly: false, singleBatch: false }),
  /** dauerhafter Hinweis fürs Lager bei jeder Lieferung an diesen Abnehmer */
  warehouseNote: z.string().trim().max(1000).nullable().default(null),
});
export type B2bCustomerInput = z.infer<typeof b2bCustomerInputSchema>;

/** Momentaufnahme am Auftrag */
export interface B2bOrderInfo {
  customerId: string;
  customerName: string;
  customerNumber: string | null;
  gln: string | null;
  purchaseOrderNumber: string;
  /** gewünschter Liefertermin (YYYY-MM-DD) */
  deliveryDate: string | null;
  /** Zeitfenster am Liefertag (HH:MM) */
  windowFrom: string | null;
  windowTo: string | null;
  shipping: B2bShipping;
  loadCarrier: B2bLoadCarrier;
  receivingHours: string | null;
  avisRequired: boolean;
  avisContact: string | null;
  requirements: B2bRequirements;
  warehouseNote: string | null;
}

export const b2bOrderFieldsSchema = z
  .object({
    customerId: z.string().uuid(),
    purchaseOrderNumber: z.string().trim().min(1).max(60),
    deliveryDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().default(null),
    windowFrom: z.string().regex(timeRe).nullable().default(null),
    windowTo: z.string().regex(timeRe).nullable().default(null),
    shipping: z.enum(B2B_SHIPPING).optional(),
    loadCarrier: z.enum(B2B_LOAD_CARRIERS).optional(),
  })
  .refine((o) => !o.windowFrom || !o.windowTo || o.windowFrom < o.windowTo, { message: "Zeitfenster: „von“ muss vor „bis“ liegen", path: ["windowTo"] })
  .refine((o) => !(o.windowFrom || o.windowTo) || o.deliveryDate, { message: "Zeitfenster nur mit Liefertermin", path: ["deliveryDate"] });

export const SHIPPING_LABEL: Record<B2bShipping, string> = { freight: "Spedition", parcel: "Paket" };
export const LOAD_CARRIER_LABEL: Record<B2bLoadCarrier, string> = { pallet: "Palette", carton: "Karton" };

const fmtDay = (ymd: string) => {
  const [y, m, d] = ymd.split("-");
  return `${d}.${m}.${y}`;
};

/** Liefertermin lesbar, z. B. „Di 14.10.2026, 08:00–12:00 Uhr“ */
export function b2bDeliveryText(i: Pick<B2bOrderInfo, "deliveryDate" | "windowFrom" | "windowTo">): string | null {
  if (!i.deliveryDate) return null;
  const wd = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"][new Date(`${i.deliveryDate}T12:00:00Z`).getUTCDay()];
  const win = i.windowFrom && i.windowTo ? `, ${i.windowFrom}–${i.windowTo} Uhr` : i.windowFrom ? `, ab ${i.windowFrom} Uhr` : i.windowTo ? `, bis ${i.windowTo} Uhr` : "";
  return `${wd} ${fmtDay(i.deliveryDate)}${win}`;
}

/** Vorgaben lesbar (für Lager und Anzeige) */
export function b2bRequirementTexts(r: B2bRequirements): string[] {
  return [
    ...(r.minShelfLifeDays ? [`MHD bei Ankunft mind. ${r.minShelfLifeDays} Tage`] : []),
    ...(r.fullCartonsOnly ? ["nur ganze Kartons"] : []),
    ...(r.singleBatch ? ["je Artikel nur eine Charge"] : []),
  ];
}

/** Hinweis fürs Lager (FFN „interne Notiz“, nur für Logiful sichtbar) – alles Wichtige auf einen Blick */
export function b2bWarehouseNote(i: B2bOrderInfo, extra?: string | null): string {
  const lines = [
    `B2B-LIEFERUNG an ${i.customerName}${i.customerNumber ? ` (Kd-Nr. ${i.customerNumber})` : ""}${i.gln ? ` · GLN ${i.gln}` : ""}`,
    `Bestell-Nr. des Händlers: ${i.purchaseOrderNumber} – auf Lieferschein und Packstücke`,
    `Versand: ${SHIPPING_LABEL[i.shipping]} auf ${LOAD_CARRIER_LABEL[i.loadCarrier]}`,
    ...(i.deliveryDate ? [`Liefertermin beim Händler: ${b2bDeliveryText(i)}`] : []),
    ...(i.receivingHours ? [`Warenannahme: ${i.receivingHours}`] : []),
    ...(i.avisRequired ? [`Anlieferung vorab anmelden${i.avisContact ? `: ${i.avisContact}` : ""}`] : []),
    ...(b2bRequirementTexts(i.requirements).length ? [`Vorgaben: ${b2bRequirementTexts(i.requirements).join(" · ")}`] : []),
    ...(i.warehouseNote ? [`Hinweis: ${i.warehouseNote}`] : []),
    ...(extra ? [`Notiz: ${extra}`] : []),
  ];
  return lines.join("\n");
}

/** FFN erwartet den Wunschtermin als Zeitpunkt – Liefertag mittags (UTC 10:00 = 11/12 Uhr deutscher Zeit, Tag bleibt in jeder Zeitzone Europas gleich) */
export const b2bDesiredDeliveryIso = (ymd: string) => `${ymd}T10:00:00Z`;

// ── Vorgaben des Händlers prüfen (vor der Übergabe ans Lager) ─────────────

/** Sperrgrund-Präfix (siehe holds.ts: Art „b2b_requirement“) */
export const B2B_HOLD_PREFIX = "Händler-Vorgabe: ";

export interface B2bCheckLot {
  batch: string | null;
  /** YYYY-MM-DD */
  bestBefore: string | null;
  /** frei (Menge − gesperrt) */
  available: number;
}
export interface B2bCheckLine {
  sku: string;
  quantity: number;
  /** Stück je Karton (Artikel → Nachbestellung) */
  packSize: number | null;
  bestBeforeManaged: boolean;
  batchManaged: boolean;
  /** Chargenbestand im Lager – leer = unbekannt (dann keine Chargenprüfung möglich) */
  lots: B2bCheckLot[];
}

const addDays = (ymd: string, days: number) => {
  const d = new Date(`${ymd}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

/** Ankunft beim Händler: Liefertermin, sonst heute + 3 Tage (übliche Laufzeit Spedition) */
export const b2bArrival = (deliveryDate: string | null, todayYmd: string) => deliveryDate ?? addDays(todayYmd, 3);

/**
 * Lassen sich die Vorgaben mit dem aktuellen Chargenbestand erfüllen? Liefert Sperrgründe (leer = alles erfüllbar).
 * Geprüft wird gegen den freien Chargenbestand im Lager (ohne Zuordnung zu anderen offenen Aufträgen) – das Lager
 * wählt die Chargen beim Kommissionieren anhand des Hinweises.
 */
export function b2bRequirementIssues(r: B2bRequirements, lines: B2bCheckLine[], arrivalYmd: string): string[] {
  const out: string[] = [];
  const cutoff = r.minShelfLifeDays ? addDays(arrivalYmd, r.minShelfLifeDays) : null;
  for (const l of lines) {
    if (r.fullCartonsOnly) {
      if (!l.packSize) out.push(`${B2B_HOLD_PREFIX}${l.sku} – Kartongröße fehlt am Artikel (Artikel → Nachbestellung → Verpackungseinheit)`);
      else if (l.quantity % l.packSize) {
        const lo = Math.floor(l.quantity / l.packSize) * l.packSize;
        out.push(`${B2B_HOLD_PREFIX}${l.sku} – ${l.quantity} Stück sind keine ganzen Kartons à ${l.packSize} (passend: ${[lo, lo + l.packSize].filter((n) => n > 0).join(" oder ")})`);
      }
    }
    if (!l.lots.length) continue;
    const usable = l.lots.filter((x) => x.available > 0 && (!cutoff || !l.bestBeforeManaged || (x.bestBefore != null && x.bestBefore >= cutoff)));
    if (cutoff && l.bestBeforeManaged) {
      const n = usable.reduce((s, x) => s + x.available, 0);
      if (n < l.quantity) out.push(`${B2B_HOLD_PREFIX}${l.sku} – nur ${n} von ${l.quantity} Stück mit MHD ab ${fmtDay(cutoff)} (mind. ${r.minShelfLifeDays} Tage bei Ankunft ${fmtDay(arrivalYmd)})`);
    }
    if (r.singleBatch && l.batchManaged) {
      const max = Math.max(0, ...usable.map((x) => x.available));
      if (max < l.quantity) out.push(`${B2B_HOLD_PREFIX}${l.sku} – keine einzelne Charge mit ${l.quantity} Stück${cutoff && l.bestBeforeManaged ? " und passendem MHD" : ""} (größte: ${max})`);
    }
  }
  return out;
}
